import React, { useState, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  Table,
  Space,
  Tag,
  Select,
  InputNumber,
  message,
  Collapse,
  Row,
  Col,
  AutoComplete,
  Typography,
  Modal,
  List,
  Empty
} from 'antd';
import { SearchOutlined, EnvironmentOutlined, PhoneOutlined, MailOutlined, RobotOutlined, FilterOutlined, DollarOutlined, SortAscendingOutlined, StarOutlined, StarFilled, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import { cachedSearch, createSearchCacheKey, searchCache } from './utils/cache';
import { trackSearch } from './utils/analytics';
import { MEILISEARCH_ENDPOINTS, MEILISEARCH_KEY } from './config/api';
import { addFavorite, removeFavorite, isFavorite, getFavorites } from './utils/favorites';

const { Search } = Input;
const { Text } = Typography;

const normalizeCNPJ = (value: string) => (value || '').replace(/\D/g, '');

// Lista completa de CNAEs (exemplo - você pode expandir)
const CNAES_COMPLETOS = [
  { value: '0111301', label: '0111301 - Cultivo de arroz' },
  { value: '0111302', label: '0111302 - Cultivo de milho' },
  { value: '0111303', label: '0111303 - Cultivo de trigo' },
  { value: '4711301', label: '4711301 - Comércio varejista de mercadorias em geral' },
  { value: '5611201', label: '5611201 - Restaurantes e similares' },
  { value: '4712100', label: '4712100 - Comércio varejista de mercadorias em lojas de conveniência' },
  { value: '8630501', label: '8630501 - Atividade médica ambulatorial' },
  { value: '6201501', label: '6201501 - Desenvolvimento de programas de computador' },
  { value: '4530701', label: '4530701 - Comércio por atacado de peças para veículos' },
  { value: '4744005', label: '4744005 - Comércio varejista de ferragens' },
  { value: '4520001', label: '4520001 - Manutenção e reparação de veículos' },
  { value: '4751201', label: '4751201 - Comércio de equipamentos de informática' },
  { value: '8630503', label: '8630503 - Atividade odontológica' },
  { value: '4711302', label: '4711302 - Comércio varejista de mercadorias em geral, com predominância de produtos alimentícios - supermercados' },
  { value: '4713001', label: '4713001 - Lojas de departamentos ou magazines' },
];

interface Company {
  id: string;
  cnpj_completo: string;
  razao_social: string;
  nome_fantasia?: string;
  situacao?: string;
  uf?: string;
  municipio_nome?: string;
  email?: string;
  telefone1?: string;
  telefone2?: string;
  capital_social?: number;
  cnae_principal?: string;
  cnae_descricao?: string;
  natureza_juridica?: string;
  porte?: string;
  cep?: string;
  tipo_logradouro?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  complemento?: string;
}

type SortField = 'razao_social' | 'capital_social' | null;
type SortOrder = 'asc' | 'desc';

interface SearchPageNewProps {
  onSelectCNPJ?: (cnpj: string) => void;
}

const SearchPageNew: React.FC<SearchPageNewProps> = ({ onSelectCNPJ }) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Company[]>([]);
  const [filteredResults, setFilteredResults] = useState<Company[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filtros
  const [cnaeFilter, setCnaeFilter] = useState('');
  const [ufFilter, setUfFilter] = useState<string | null>(null);
  const [situacaoFilter, setSituacaoFilter] = useState<string[]>([]);
  const [capitalMin, setCapitalMin] = useState<number | null>(null);
  const [capitalMax, setCapitalMax] = useState<number | null>(null);
  
  // Ordenação
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  
  // Visualização
  const [showSituacao, setShowSituacao] = useState<string[]>(['ATIVA', 'INAPTA', 'BAIXADA', 'SUSPENSA']);
  
  // Favoritos
  const [favorites, setFavorites] = useState<string[]>([]);
  
  // States para modal de sócios
  const [sociosModalVisible, setSociosModalVisible] = useState(false);
  const [selectedCnpj, setSelectedCnpj] = useState<string | null>(null);
  const [loadingSocios, setLoadingSocios] = useState(false);
  const [sociosData, setSociosData] = useState<any[]>([]);
  
  // Carregar favoritos ao montar
  useEffect(() => {
    const loadFavorites = () => {
      try {
        const fav = getFavorites();
        setFavorites(
          fav
            .map((f: any) => normalizeCNPJ(f.cnpj_completo || f.cnpj_basico || ''))
            .filter(Boolean)
        );
      } catch (error) {
        console.error('Erro ao carregar favoritos:', error);
      }
    };
    loadFavorites();
  }, []);

  const isFavForCompany = (company: Company) => {
    const cnpj = normalizeCNPJ(company.cnpj_completo);
    if (!cnpj) return false;
    const basico = cnpj.substring(0, 8);
    return favorites.some((f) => cnpj.startsWith(f) || basico === f);
  };
  
  const handleToggleFavorite = (company: Company) => {
    const cnpj = normalizeCNPJ(company.cnpj_completo);
    const isFav = isFavorite(cnpj);
    const basico = cnpj.substring(0, 8);
    
    if (isFav) {
      if (removeFavorite(cnpj)) {
        setFavorites(prev => prev.filter(c => c !== cnpj && c !== basico));
        message.success('Empresa removida dos favoritos!');
      }
    } else {
      if (addFavorite(company)) {
        setFavorites(prev => [...prev, cnpj]);
        message.success('Empresa adicionada aos favoritos!');
      } else {
        message.warning('Esta empresa já está nos favoritos');
      }
    }
  };

  const carregarSocios = async (cnpj: string) => {
    setLoadingSocios(true);
    setSelectedCnpj(cnpj);
    try {
      const API_BASE = window.location.hostname === 'localhost' 
        ? 'http://127.0.0.1:8000/api/v1'
        : '/api/v1';
      
      const response = await axios.get(`${API_BASE}/socios/cnpj/${cnpj}`);
      if (response.data && response.data.socios && response.data.socios.length > 0) {
        setSociosData(response.data.socios);
        setSociosModalVisible(true);
      } else {
        message.info('Empresa sem sócios registrados (MEI ou empresa individual)');
      }
    } catch (error) {
      console.error('Erro ao carregar sócios:', error);
      message.error('Erro ao carregar sócios');
    } finally {
      setLoadingSocios(false);
    }
  };

  const handleSearch = async (query?: string) => {
    const searchTerm = query || searchQuery;
    
    if (!searchTerm && !cnaeFilter && !ufFilter && situacaoFilter.length === 0) {
      message.warning('Digite algo para buscar ou aplique filtros');
      return;
    }

    setLoading(true);

    try {
      const filters: string[] = [];
      
      // Detectar se é um CNPJ (apenas números, 8 ou 14 dígitos)
      const cnpjPattern = /^\d{8}$|^\d{14}$/;
      const cleanSearchTerm = searchTerm.replace(/\D/g, '');
      const isCNPJSearch = cnpjPattern.test(cleanSearchTerm);
      
      // Se for CNPJ de 14 dígitos, extrair apenas os 8 primeiros (CNPJ Básico)
      const cnpjBasico = isCNPJSearch && cleanSearchTerm.length === 14 
        ? cleanSearchTerm.substring(0, 8) 
        : cleanSearchTerm;
      
      if (cnaeFilter) {
        // Extrair apenas números do CNAE
        const cnaeNumero = cnaeFilter.replace(/\D/g, '').substring(0, 7);
        if (cnaeNumero) {
          filters.push(`cnae_principal = "${cnaeNumero}"`);
        }
      }
      
      if (ufFilter) {
        filters.push(`uf = "${ufFilter}"`);
      }
      
      if (capitalMin !== null) {
        filters.push(`capital_social >= ${capitalMin}`);
      }
      
      if (capitalMax !== null) {
        filters.push(`capital_social <= ${capitalMax}`);
      }

      // NÃO adicionar filtro de CNPJ - usar busca textual que funciona melhor

      // Ordenação no Meilisearch
      const sort: string[] = [];
      
      if (sortField === 'razao_social') {
        sort.push(`razao_social:${sortOrder}`);
      } else if (sortField === 'capital_social') {
        sort.push(`capital_social:${sortOrder}`);
      }

      // Criar chave de cache
      const cacheKey = createSearchCacheKey({
        query: searchTerm,
        filters: filters,
        sort: sort,
        limit: 200
      });

      // Buscar com cache (5 minutos de TTL)
      const response = await cachedSearch(
        cacheKey,
        async () => {
          const searchParams: any = {
            // Se for CNPJ, usar o CNPJ básico (8 dígitos) na busca textual
            q: isCNPJSearch ? cnpjBasico : searchTerm,
            limit: 200,  // Buscar mais para compensar filtro client-side
          };
          
          // Adicionar filtros apenas se existirem
          if (filters.length > 0) {
            searchParams.filter = filters.join(' AND ');
          }
          
          // Adicionar ordenação apenas se existir
          if (sort.length > 0) {
            searchParams.sort = sort;
          }
          
          return await axios.post(MEILISEARCH_ENDPOINTS.SEARCH_EMPRESAS, searchParams, {
            headers: {
              'Authorization': `Bearer ${MEILISEARCH_KEY}`
            }
          });
        },
        5 * 60 * 1000 // 5 minutos
      );

      // Filtrar por situação no client-side
      let filteredHits = response.data.hits;
      if (situacaoFilter.length > 0) {
        filteredHits = filteredHits.filter((hit: Company) => 
          situacaoFilter.includes(hit.situacao || '')
        );
      }
      
      // Se for busca por CNPJ, ordenar para colocar match exato primeiro
      if (isCNPJSearch) {
        filteredHits = filteredHits.sort((a: Company, b: Company) => {
          const aExact = a.cnpj_completo?.startsWith(cnpjBasico);
          const bExact = b.cnpj_completo?.startsWith(cnpjBasico);
          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;
          return 0;
        });
      }

      setResults(filteredHits);
      applyFiltersAndSort(filteredHits);
      
      // Mostrar estatísticas do cache
      const stats = searchCache.getStats();
      console.log(`📊 Cache: ${stats.size}/${stats.maxSize} (${stats.usage})`);
      
      if (response.data.hits.length === 0) {
        message.info('Nenhuma empresa encontrada');
      } else {
        message.success(`${filteredHits.length} empresas encontradas`);
        
        // Track da busca
        trackSearch(searchTerm, {
          cnae: cnaeFilter,
          uf: ufFilter,
          situacao: situacaoFilter,
          capitalMin,
          capitalMax,
          resultsCount: filteredHits.length
        });
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      message.error('Erro ao buscar empresas');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = (data: Company[]) => {
    let filtered = [...data];
    
    // Filtrar por situação (mostrar/ocultar)
    filtered = filtered.filter(company => 
      showSituacao.includes(company.situacao || '')
    );
    
    // Ordenação local (complementar)
    if (sortField) {
      filtered.sort((a, b) => {
        let aVal: any = a[sortField];
        let bVal: any = b[sortField];
        
        if (sortField === 'razao_social') {
          aVal = (aVal as string || '').toLowerCase();
          bVal = (bVal as string || '').toLowerCase();
        }
        
        // Tratar valores undefined/null
        if (aVal === undefined || aVal === null) aVal = '';
        if (bVal === undefined || bVal === null) bVal = '';
        
        if (aVal === bVal) return 0;
        
        const comparison = aVal > bVal ? 1 : -1;
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }
    
    setFilteredResults(filtered);
  };

  // Aplicar filtros quando mudar visualização
  useEffect(() => {
    if (results.length > 0) {
      applyFiltersAndSort(results);
    }
  }, [sortField, sortOrder, situacaoFilter]);

  const columns = [
    {
      title: 'CNPJ',
      dataIndex: 'cnpj_completo',
      key: 'cnpj_completo',
      width: 160,
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace' }}>
          {text?.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
        </span>
      )
    },
    {
      title: 'Razão Social',
      dataIndex: 'razao_social',
      key: 'razao_social',
      ellipsis: true,
    },
    {
      title: 'Contato',
      key: 'contato',
      width: 250,
      render: (_: any, record: Company) => (
        <Space direction="vertical" size="small">
          {record.telefone1 && record.telefone1.trim() !== '() ' && (
            <div>
              <PhoneOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              <a 
                href={`https://wa.me/55${record.telefone1.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontFamily: 'monospace' }}
              >
                {record.telefone1}
              </a>
            </div>
          )}
          {record.email && (
            <div>
              <MailOutlined style={{ marginRight: 8, color: '#52c41a' }} />
              <a href={`mailto:${record.email}`} style={{ fontSize: '12px' }}>
                {record.email}
              </a>
            </div>
          )}
          {(!record.telefone1 || record.telefone1.trim() === '() ') && !record.email && (
            <span style={{ color: '#999' }}>-</span>
          )}
        </Space>
      )
    },
    {
      title: 'Situação',
      dataIndex: 'situacao',
      key: 'situacao',
      width: 100,
      render: (text: string) => {
        const color = text === 'ATIVA' ? 'green' : text === 'BAIXADA' ? 'red' : text === 'INAPTA' ? 'orange' : 'default';
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: 'Capital Social',
      dataIndex: 'capital_social',
      key: 'capital_social',
      width: 150,
      render: (value: number) => (
        <span style={{ fontWeight: 600 }}>
          {value ? `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
        </span>
      )
    },
    {
      title: 'Localização',
      key: 'localizacao',
      width: 200,
      render: (_: any, record: Company) => {
        const endereco = [
          record.logradouro,
          record.numero,
          record.bairro,
          record.municipio_nome,
          record.uf,
          record.cep
        ].filter(Boolean).join(', ');
        
        const enderecoMaps = endereco || `${record.municipio_nome}, ${record.uf}`;
        
        return (
          <a 
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(enderecoMaps)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '12px' }}
          >
            <EnvironmentOutlined style={{ marginRight: 4 }} />
            {record.municipio_nome}/{record.uf}
          </a>
        );
      }
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 170,
      fixed: 'right' as const,
      render: (_: any, record: Company) => {
        const isFav = isFavForCompany(record);
        return (
          <Space size="small">
            <Button
              type={isFav ? 'primary' : 'default'}
              icon={isFav ? <StarFilled /> : <StarOutlined />}
              onClick={() => handleToggleFavorite(record)}
              title={isFav ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
              style={{
                background: isFav ? '#faad14' : undefined,
                borderColor: isFav ? '#faad14' : undefined
              }}
            />
            <Button
              icon={<UserOutlined />}
              onClick={() => carregarSocios(record.cnpj_completo || '')}
              title="Ver sócios"
              loading={loadingSocios && selectedCnpj === record.cnpj_completo}
            />
            <Button
              icon={<SearchOutlined />}
              onClick={() => onSelectCNPJ?.(record.cnpj_completo)}
              title="Abrir detalhes"
            />
          </Space>
        );
      }
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {filteredResults.length === 0 && !loading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}>
          <h1 style={{
            fontSize: '52px',
            marginBottom: '32px',
            color: '#202124',
            fontWeight: 400,
          }}>
            Data Econômica
          </h1>
          
          <div style={{ width: '700px', maxWidth: '90%' }}>
            <Search
              placeholder="Digite CNPJ, razão social, email, telefone..."
              enterButton={
                <Button type="primary" size="large" style={{ height: '44px', minWidth: '120px' }}>
                  <SearchOutlined /> Buscar
                </Button>
              }
              size="large"
              onSearch={handleSearch}
              loading={loading}
              style={{ marginBottom: '16px' }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            
            <Collapse 
              ghost
              items={[{
                key: '1',
                label: <span><FilterOutlined /> Filtros Avançados</span>,
                children: (
                  <div style={{ 
                    padding: '20px', 
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #dadce0',
                  }}>
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                          CNAE (Nome ou Número):
                        </label>
                        <AutoComplete
                          style={{ width: '100%' }}
                          options={CNAES_COMPLETOS}
                          placeholder="Digite código ou descrição..."
                          value={cnaeFilter}
                          onChange={(value) => setCnaeFilter(value)}
                          filterOption={(inputValue, option) =>
                            option!.label.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                          }
                        />
                      </Col>
                      
                      <Col span={12}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                          Estado (UF):
                        </label>
                        <Select 
                          style={{ width: '100%' }}
                          placeholder="Selecione"
                          value={ufFilter}
                          onChange={(value) => setUfFilter(value)}
                          allowClear
                          showSearch
                          options={[
                            { value: 'SP', label: 'São Paulo' },
                            { value: 'RJ', label: 'Rio de Janeiro' },
                            { value: 'MG', label: 'Minas Gerais' },
                            { value: 'RS', label: 'Rio Grande do Sul' },
                            { value: 'PR', label: 'Paraná' },
                            { value: 'SC', label: 'Santa Catarina' },
                          ]}
                        />
                      </Col>
                      
                      <Col span={12}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                          Situação:
                        </label>
                        <Select 
                          mode="multiple"
                          style={{ width: '100%' }}
                          placeholder="Selecione uma ou mais"
                          value={situacaoFilter}
                          onChange={(value) => setSituacaoFilter(value)}
                          options={[
                            { value: 'ATIVA', label: 'Ativa' },
                            { value: 'INAPTA', label: 'Inapta' },
                            { value: 'BAIXADA', label: 'Baixada' },
                            { value: 'SUSPENSA', label: 'Suspensa' },
                          ]}
                        />
                      </Col>
                      
                      <Col span={12}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                          <DollarOutlined /> Capital Social:
                        </label>
                        <Space.Compact style={{ width: '100%' }}>
                          <InputNumber
                            style={{ width: '50%' }}
                            placeholder="De"
                            value={capitalMin}
                            onChange={(value) => setCapitalMin(value)}
                            formatter={value => `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => {
                              if (!value) return 0;
                              const num = value.replace(/R\$\s?|(,*)/g, '');
                              return Number(num) || 0;
                            }}
                          />
                          <InputNumber
                            style={{ width: '50%' }}
                            placeholder="Até"
                            value={capitalMax}
                            onChange={(value) => setCapitalMax(value)}
                            formatter={value => `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => {
                              if (!value) return 0;
                              const num = value.replace(/R\$\s?|(,*)/g, '');
                              return Number(num) || 0;
                            }}
                          />
                        </Space.Compact>
                      </Col>
                    </Row>
                  </div>
                )
              }]}
            />
          </div>
        </div>
      )}

      {filteredResults.length > 0 && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Space.Compact style={{ width: '100%' }}>
                  <Search
                    placeholder="Digite CNPJ, razão social, email, telefone..."
                    enterButton={<Button type="primary"><SearchOutlined /> Buscar</Button>}
                    onSearch={handleSearch}
                    loading={loading}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: '100%' }}
                  />
                  <Button
                    type="default"
                    icon={<RobotOutlined />}
                    onClick={() => {
                      const query = searchQuery || 'empresas';
                      window.location.href = `/chat?q=${encodeURIComponent(query)}`;
                    }}
                    style={{ marginLeft: 8 }}
                  >
                    Buscar com IA
                  </Button>
                </Space.Compact>
              </Col>
              
              <Col>
                <Space>
                  <Select
                    style={{ width: 200 }}
                    placeholder="Ordenar por"
                    value={sortField ? `${sortField}_${sortOrder}` : null}
                    onChange={(value) => {
                      if (value) {
                        const [field, order] = value.split('_');
                        setSortField(field as SortField);
                        setSortOrder(order as SortOrder);
                      } else {
                        setSortField(null);
                      }
                    }}
                    allowClear
                    options={[
                      { value: 'razao_social_asc', label: <><SortAscendingOutlined /> Nome A-Z</> },
                      { value: 'razao_social_desc', label: 'Nome Z-A' },
                      { value: 'capital_social_desc', label: 'Capital Maior-Menor' },
                      { value: 'capital_social_asc', label: 'Capital Menor-Maior' },
                    ]}
                  />
                  
                  <Select
                    mode="multiple"
                    style={{ width: 250 }}
                    placeholder="Mostrar/Ocultar"
                    value={showSituacao}
                    onChange={(value) => setShowSituacao(value)}
                    options={[
                      { value: 'ATIVA', label: '✓ Ativas' },
                      { value: 'INAPTA', label: '✓ Inaptas' },
                      { value: 'BAIXADA', label: '✓ Baixadas' },
                      { value: 'SUSPENSA', label: '✓ Suspensas' },
                    ]}
                  />
                </Space>
              </Col>
            </Row>
          </div>

          <Card>
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Tag color="blue">Total: {filteredResults.length} empresas</Tag>
                <Tag color="green">Ativas: {filteredResults.filter(r => r.situacao === 'ATIVA').length}</Tag>
                <Tag color="orange">Inaptas: {filteredResults.filter(r => r.situacao === 'INAPTA').length}</Tag>
                <Tag color="red">Baixadas: {filteredResults.filter(r => r.situacao === 'BAIXADA').length}</Tag>
              </Space>
            </div>
            
            <Table
              columns={columns}
              dataSource={filteredResults}
              rowKey="cnpj_completo"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} empresas`,
              }}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ padding: '16px', background: '#fafafa' }}>
                    <Row gutter={[16, 16]}>
                      <Col span={8}>
                        <Space direction="vertical" size="small">
                          <Text strong>📧 Email:</Text>
                          <Text copyable={!!record.email}>{record.email || 'Não informado'}</Text>
                        </Space>
                      </Col>
                      <Col span={8}>
                        <Space direction="vertical" size="small">
                          <Text strong>📞 Telefone:</Text>
                          <Text copyable={!!record.telefone1}>{record.telefone1 || 'Não informado'}</Text>
                        </Space>
                      </Col>
                      <Col span={8}>
                        <Space direction="vertical" size="small">
                          <Text strong>📍 Endereço:</Text>
                          <Text>
                            {record.logradouro && record.numero 
                              ? `${record.logradouro}, ${record.numero}${record.bairro ? ` - ${record.bairro}` : ''}`
                              : 'Não informado'}
                          </Text>
                        </Space>
                      </Col>
                      <Col span={8}>
                        <Space direction="vertical" size="small">
                          <Text strong>🏙️ Município/UF:</Text>
                          <Text>{record.municipio_nome || 'N/A'} - {record.uf}</Text>
                        </Space>
                      </Col>
                      <Col span={8}>
                        <Space direction="vertical" size="small">
                          <Text strong>📮 CEP:</Text>
                          <Text copyable={!!record.cep}>{record.cep || 'Não informado'}</Text>
                        </Space>
                      </Col>
                      <Col span={8}>
                        <Space direction="vertical" size="small">
                          <Text strong>🏢 CNAE Principal:</Text>
                          <Text>{record.cnae_principal || 'Não informado'}</Text>
                        </Space>
                      </Col>
                    </Row>
                  </div>
                ),
              }}
            />
          </Card>
        </>
      )}
      
      {/* Modal de Sócios */}
      <Modal
        title={`👥 Quadro Societário - CNPJ ${selectedCnpj}`}
        open={sociosModalVisible}
        onCancel={() => {
          setSociosModalVisible(false);
          setSelectedCnpj(null);
          setSociosData([]);
        }}
        footer={null}
        width={800}
      >
        {sociosData.length > 0 ? (
          <List
            dataSource={sociosData}
            renderItem={(socio: any) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />}
                  title={<Text strong>{socio.nome}</Text>}
                  description={
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div>
                        <Tag>CPF: {socio.cpf_cnpj_masked}</Tag>
                        <Tag color="blue">{socio.qualificacao}</Tag>
                      </div>
                      {socio.data_entrada && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Data entrada: {socio.data_entrada}
                        </Text>
                      )}
                      {socio.percentual_capital && socio.percentual_capital > 0 && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Participação: {socio.percentual_capital}%
                        </Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty description="Empresa sem sócios registrados" />
        )}
      </Modal>
    </div>
  );
};

export default SearchPageNew;
